"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";
import { initializeCSRF } from "@/lib/csrf-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { HardHat, Building2 } from "lucide-react";
import type { UserType } from "@/lib/types";

export default function SignupPage() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [userType, setUserType] = useState<UserType | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const supabase = createClient();

  // Phone number validation — AU for contractors, international for workers
  const validatePhone = (phone: string): boolean => {
    const digits = phone.replace(/\D/g, '');
    
    // Australian mobile: 04xx xxx xxx or +614xxxxxxxx
    if (digits.length === 10 && digits.startsWith('04')) return true;
    if (digits.length === 11 && digits.startsWith('614')) return true;
    
    // International numbers for workers (WHV backpackers)
    if (userType === 'worker') {
      const cleaned = phone.replace(/[\s\-()]/g, '');
      if (/^\+[1-9]\d{7,14}$/.test(cleaned)) return true;
    }
    
    return false;
  };

  async function handleSignup(e: React.FormEvent) {
    e.preventDefault();
    if (!userType) {
      setError("Please select whether you're hiring or looking for work");
      return;
    }
    if (!agreedToTerms) {
      setError("Please agree to the Terms of Service and Privacy Policy");
      return;
    }

    // Confirm password match
    if (password !== confirmPassword) {
      setError("Passwords don't match. Please check and try again.");
      return;
    }
    
    // Name is required
    if (!fullName.trim()) {
      setError("Please enter your full name.");
      return;
    }

    // Phone is required
    if (!phone.trim()) {
      setError("Phone number is required so we can reach you about jobs and updates.");
      return;
    }
    if (!validatePhone(phone)) {
      setError(
        userType === 'worker'
          ? "Please enter a valid phone number (e.g. 04xx xxx xxx or +44 7xxxxxxxxx)"
          : "Please enter a valid Australian phone number (04xx xxx xxx or +614xxxxxxxx)"
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { 
            type: userType,
            phone: phone || null,
            name: fullName.trim(),
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (signUpError) {
        // With autoconfirm enabled, Supabase returns "User already registered" for
        // duplicate emails instead of a fake user object with empty identities.
        // Show a helpful message instead of the raw error.
        if (signUpError.message.includes("already registered")) {
          setError("An account with this email already exists. Try signing in or resetting your password.");
        } else {
          setError(signUpError.message);
        }
        setLoading(false);
        return;
      }

      // If email is already confirmed (autoconfirm enabled), go straight to onboarding
      if (data.user?.email_confirmed_at) {
        await initializeCSRF();
        const onboardingPath = userType === "contractor"
          ? "/contractor/signup"
          : "/worker/signup";
        // Use window.location for a full-page navigation to avoid race conditions
        // between router.push and middleware redirecting verified users off auth pages
        window.location.href = onboardingPath;
      } else {
        // Email confirmation required — send to verify page
        router.push("/auth/verify-email");
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setError(message);
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Create your account</CardTitle>
          <CardDescription>
            Sign up to find work or hire workers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup} className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  placeholder="e.g. Michael McLoughlin"
                  className="h-12 text-base"
                  autoComplete="name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="h-12 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Create a password"
                  minLength={8}
                  autoComplete="new-password"
                  className="h-12 text-base pr-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <PasswordInput
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Type it again"
                  minLength={8}
                  autoComplete="new-password"
                  className="h-12 text-base pr-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  placeholder="04xx xxx xxx"
                  className="h-12 text-base"
                />
                <p className="text-xs text-muted-foreground">
                  We&apos;ll text you about jobs and updates. Australian mobiles only.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>I am a...</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={userType === "contractor" ? "default" : "outline"}
                  onClick={() => setUserType("contractor")}
                  className="h-24 flex-col gap-2"
                >
                  <Building2 className="h-6 w-6" />
                  <span>Contractor</span>
                  <span className="text-xs font-normal opacity-80">Hiring workers</span>
                </Button>
                <Button
                  type="button"
                  variant={userType === "worker" ? "default" : "outline"}
                  onClick={() => setUserType("worker")}
                  className="h-24 flex-col gap-2"
                >
                  <HardHat className="h-6 w-6" />
                  <span>Worker</span>
                  <span className="text-xs font-normal opacity-80">Looking for work</span>
                </Button>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border accent-primary"
              />
              <Label htmlFor="terms" className="text-sm font-normal leading-snug text-muted-foreground cursor-pointer">
                I agree to the{" "}
                <Link href="/legal/terms-of-service" className="text-primary underline hover:no-underline min-h-[44px] inline-flex items-center" target="_blank">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link href="/legal/privacy-policy" className="text-primary underline hover:no-underline min-h-[44px] inline-flex items-center" target="_blank">
                  Privacy Policy
                </Link>
              </Label>
            </div>

            {error && (
              <div className="rounded-lg bg-destructive/10 p-3">
                <p className="text-sm text-destructive font-medium">{error}</p>
              </div>
            )}

            <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={loading || !agreedToTerms}>
              {loading ? "Creating account..." : "Create account"}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link href="/auth/login" className="text-primary underline hover:no-underline inline-flex items-center min-h-[44px] py-3">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
